import React, { useState, useEffect } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { BASE_URL } from "../../Config";
import Swal from "sweetalert2";
import { Card, Row, Col, Button, Form, InputGroup, Spinner, Modal } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../Pages/Company/Company.css";
import Register_Supervisor from "./Register_Supervisor";
import ResetPasswordModal from "./ResetPasswordModal";

const Company_Supervisor = ({
  companyUserId: companyUserIdProp,
  companyName: companyNameProp,
  allowRegister = true,
}) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [userId, setUserId] = useState(companyUserIdProp || null);
  const [companyName, setCompanyName] = useState(companyNameProp || "");
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  const fetchSupervisors = async (companyUserId) => {
    if (!companyUserId) {
      setAgents([]);
      return;
    }
    try {
      const response = await axios.get(`${BASE_URL}Company_Supervisor?userId=${companyUserId}`, {
        headers: { accept: "*/*" },
      });

      if (response.data.responseCode === "0000") {
        setAgents(response.data.agents || []);
      } else {
        setAgents([]);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
      Swal.fire("Error", "Failed to load supervisors.", "error");
      setAgents([]);
    }
  };

  useEffect(() => {
    if (companyUserIdProp) {
      setUserId(companyUserIdProp);
      setCompanyName(companyNameProp || "");
      return;
    }

    const storedUserData = sessionStorage.getItem("user");
    if (storedUserData) {
      const userData = JSON.parse(storedUserData);
      if (userData.userRole === "Manager") {
        setUserId(userData.selectedCompanyId || userData.companyId || "");
        setCompanyName(userData.selectedCompanyName || userData.companyName || "");
      } else {
        setUserId(userData.userId);
        setCompanyName(
          userData.selectedCompanyName || userData.companyName || userData.name || ""
        );
      }
    } else {
      Swal.fire("Error", "Your session has expired. Please sign in again.", "error");
    }
  }, [companyUserIdProp, companyNameProp]);

  useEffect(() => {
    const load = async () => {
      if (!userId) {
        setAgents([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      await fetchSupervisors(userId);
      setLoading(false);
    };
    load();
  }, [userId]);

  const filteredAgents = agents.filter((agent) => {
    const matchStatus = statusFilter === "All" || agent.status === statusFilter;
    const matchName = agent.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchStatus && matchName;
  });

  const handleStatusSwitch = async (agentId, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "In-Active" : "Active";

    try {
      setAgents((prev) =>
        prev.map((agent) =>
          agent.agentId === agentId ? { ...agent, status: newStatus } : agent
        )
      );

      await axios.post(
        `${BASE_URL}Change_Status`,
        { agentId, status: newStatus },
        { headers: { "Content-Type": "application/json", accept: "*/*" } }
      );
    } catch (error) {
      console.error("Error changing status:", error);
      setAgents((prev) =>
        prev.map((agent) =>
          agent.agentId === agentId ? { ...agent, status: currentStatus } : agent
        )
      );
      Swal.fire("Error", "Failed to update status.", "error");
    }
  };

  const handleEditField = (agentId) => {
    setAgents((prev) =>
      prev.map((agent) =>
        agent.agentId === agentId ? { ...agent, isEditing: true } : agent
      )
    );
  };

  const handleFieldChange = (agentId, field, value) => {
    setAgents((prev) =>
      prev.map((agent) =>
        agent.agentId === agentId ? { ...agent, [field]: value } : agent
      )
    );
  };

  const handleSaveField = async (agentId, field, value) => {
    try {
      await axios.post(
        `${BASE_URL}Edit_SupervisorDetails`,
        {
          userId: agentId,
          agentName: field === "name" ? value : undefined,
          cnic: field === "cnic" ? value : undefined,
          contactNumber: field === "contactNo" ? value : undefined,
        },
        { headers: { "Content-Type": "application/json", accept: "*/*" } }
      );

      setAgents((prev) =>
        prev.map((agent) =>
          agent.agentId === agentId ? { ...agent, isEditing: false } : agent
        )
      );
    } catch (error) {
      console.error("Error updating agent:", error);
      Swal.fire("Error", "Failed to save changes.", "error");
    }
  };

  const openResetPassword = (row) => {
    const allowed = agents.some((a) => a.agentId === row.agentId);
    if (!allowed) {
      Swal.fire(
        "Error",
        "You can only reset passwords for your company supervisors.",
        "error"
      );
      return;
    }
    const email = row.email || row.userEmail || row.Email;
    if (!email) {
      Swal.fire(
        "Error",
        "Email is not available for this supervisor. Password cannot be reset.",
        "error"
      );
      return;
    }
    setResetTarget({ email, name: row.name });
  };

  const columns = [
    {
      name: "Supervisor Name",
      selector: (row) =>
        row.isEditing ? (
          <Form.Control
            size="sm"
            value={row.name || ""}
            onChange={(e) => handleFieldChange(row.agentId, "name", e.target.value)}
            onBlur={() => handleSaveField(row.agentId, "name", row.name)}
            onKeyDown={(e) =>
              e.key === "Enter" && handleSaveField(row.agentId, "name", row.name)
            }
          />
        ) : (
          <span
            role="button"
            className="text-decoration-underline-hover"
            onDoubleClick={() => handleEditField(row.agentId)}
            title="Double-click to edit"
          >
            {row.name}
          </span>
        ),
      sortable: true,
      wrap: true,
    },
    {
      name: "Email",
      selector: (row) => row.email || row.userEmail || row.Email || "-",
      sortable: true,
      wrap: true,
      hide: "md",
    },
    {
      name: "CNIC",
      selector: (row) =>
        row.isEditing ? (
          <Form.Control
            size="sm"
            value={row.cnic || ""}
            onChange={(e) => handleFieldChange(row.agentId, "cnic", e.target.value)}
            onBlur={() => handleSaveField(row.agentId, "cnic", row.cnic)}
            onKeyDown={(e) =>
              e.key === "Enter" && handleSaveField(row.agentId, "cnic", row.cnic)
            }
          />
        ) : (
          <span onDoubleClick={() => handleEditField(row.agentId)}>{row.cnic}</span>
        ),
      sortable: true,
    },
    {
      name: "Contact No",
      selector: (row) =>
        row.isEditing ? (
          <Form.Control
            size="sm"
            value={row.contactNo || ""}
            onChange={(e) => handleFieldChange(row.agentId, "contactNo", e.target.value)}
            onBlur={() => handleSaveField(row.agentId, "contactNo", row.contactNo)}
            onKeyDown={(e) =>
              e.key === "Enter" &&
              handleSaveField(row.agentId, "contactNo", row.contactNo)
            }
          />
        ) : (
          <span onDoubleClick={() => handleEditField(row.agentId)}>{row.contactNo}</span>
        ),
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => (
        <span className={`badge ${row.status === "Active" ? "text-bg-success" : "text-bg-secondary"}`}>
          {row.status}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <Button
            size="sm"
            variant="outline-primary"
            title="Reset password"
            onClick={() => openResetPassword(row)}
          >
            <i className="bi bi-key" />
          </Button>
          <label className="switch mb-0">
            <input
              type="checkbox"
              checked={row.status === "Active"}
              onChange={() => handleStatusSwitch(row.agentId, row.status)}
            />
            <span className="slider round" />
          </label>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: "120px",
    },
  ];

  return (
    <Card className="co-card">
      <Card.Header>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-person-badge me-2 text-primary" />
              Company Supervisors
            </h5>
            <small className="text-muted">
              {companyName ? `Company: ${companyName}. ` : ""}
              Double-click a field to edit supervisor details
            </small>
          </div>
          {allowRegister && (
            <Button variant="success" size="sm" onClick={() => setShowRegisterModal(true)}>
              <i className="bi bi-person-plus me-1" />
              Add Supervisor
            </Button>
          )}
        </div>

        <Row className="g-2 align-items-center">
          <Col xs={12} md={7}>
            <div className="d-flex flex-wrap gap-2">
              {["Active", "In-Active", "All"].map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={statusFilter === status ? "primary" : "outline-secondary"}
                  onClick={() => setStatusFilter(status)}
                >
                  {status}
                </Button>
              ))}
            </div>
          </Col>
          <Col xs={12} md={5}>
            <InputGroup>
              <InputGroup.Text>
                <i className="bi bi-search" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by supervisor name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </Col>
        </Row>
      </Card.Header>

      <Card.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-2 mb-0">Loading supervisors...</p>
          </div>
        ) : (
          <div className="table-responsive">
            <DataTable
              columns={columns}
              data={filteredAgents}
              pagination
              highlightOnHover
              responsive
              striped
              noDataComponent={
                <div className="py-4 text-muted">
                  <i className="bi bi-inbox fs-3 d-block mb-2" />
                  No supervisors found
                </div>
              }
            />
          </div>
        )}
      </Card.Body>

      {allowRegister && (
      <Modal
        show={showRegisterModal}
        onHide={() => setShowRegisterModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-person-plus me-2" />
            Register Supervisor
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Register_Supervisor
            companyUserId={userId}
            companyName={companyName}
            embedded
            onSuccess={async () => {
              setShowRegisterModal(false);
              await fetchSupervisors(userId);
            }}
          />
        </Modal.Body>
      </Modal>
      )}

      <ResetPasswordModal
        show={!!resetTarget}
        onHide={() => setResetTarget(null)}
        email={resetTarget?.email}
        displayName={resetTarget?.name}
        roleLabel="supervisor"
      />
    </Card>
  );
};

export default Company_Supervisor;
